import { ArrowUpRight } from "lucide-react";
import { useMagnetic } from "@/components/effects";

const ABOUT_IMAGE = "/logos/team.jpg";

const BIO_LINES = [
  {
    n: "01",
    text: "Erik is the 20-year engineer.",
    href: "https://primeautomationsolutions.com/",
    testId: "about-bio-erik-link",
  },
  {
    n: "02",
    text: "Robert is the experience designer.",
    href: "https://www.robertrichman.com",
    testId: "about-bio-robert-link",
  },
];

export default function About() {
  const magRef = useMagnetic(0.3);
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            About · The People
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 via-transparent to-transparent pointer-events-none" />
              <img
                src={ABOUT_IMAGE}
                alt="Robert Richman and Erik Anderson"
                data-testid="about-image"
                className="relative w-full h-auto border border-white/10 block"
              />
              <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-yellow-400 bg-black/70 px-2 py-1 border border-yellow-400/30">
                The Team
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              <div className="bg-[#050505] p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Engineering
                </div>
                <div className="font-heading text-base font-light text-white">Erik Anderson</div>
                <div className="text-xs text-zinc-400 font-light mt-1">20 years shipping software</div>
              </div>
              <div className="bg-[#050505] p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Experience
                </div>
                <div className="font-heading text-base font-light text-white">Robert Richman</div>
                <div className="text-xs text-zinc-400 font-light mt-1">Designing what people feel</div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-7">
            <h2
              data-testid="about-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug text-white"
            >
              The Invisible System Design Team
            </h2>
            <p
              data-testid="about-subtitle"
              className="mt-4 font-heading text-xl sm:text-2xl font-light tracking-tight text-zinc-500"
            >
              We're people who love to build.
            </p>

            <div className="mt-10 space-y-5 max-w-xl">
              {BIO_LINES.map((b) => (
                <a
                  key={b.n}
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={b.testId}
                  className="group flex gap-4 text-base sm:text-lg leading-relaxed text-zinc-300 font-light hover:text-yellow-400 transition-colors"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400 shrink-0 pt-2 w-10">
                    {b.n}
                  </span>
                  <span className="inline-flex items-center gap-2 underline-offset-4 decoration-yellow-400/40 group-hover:decoration-yellow-400 group-hover:underline">
                    {b.text}
                    <ArrowUpRight
                      className="w-4 h-4 text-yellow-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={2}
                    />
                  </span>
                </a>
              ))}
              <p className="flex gap-4 text-base sm:text-lg leading-relaxed text-white font-light border-t border-white/10 pt-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400 shrink-0 pt-2 w-10">03</span>
                <span className="italic">We're so excited to build your idea!</span>
              </p>
            </div>

            <div className="mt-12">
              <a
                ref={magRef}
                href="sms:+13104295413?&body=Hey%20Robbe!%20I'm%20so%20intrigued%2C%20what%20do%20you%20need%20for%20me%20to%20start%3F"
                data-testid="about-cta-button"
                className="group inline-flex items-center gap-3 bg-yellow-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-medium hover:bg-yellow-500"
              >
                Say hi
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
